import React from "react"
import { db } from "@/lib/firebase"
import { useAuth } from "@/AuthContext"
import { collection, onSnapshot, query, where, limit, deleteDoc, doc } from "firebase/firestore"
import NotificationCard from "./NotificationCard"

export default function NotificationModal({ open, onClose }) {
  const { user } = useAuth()
  const uid = user?.uid || ""
  const [notifs, setNotifs] = React.useState([])
  const [deleting, setDeleting] = React.useState(null)

  React.useEffect(() => {
    if (!open || !uid) return
    const q1 = query(collection(db, "notifications"), where("uid", "==", uid), limit(100))
    const q2 = query(collection(db, "notifications"), where("riderUid", "==", uid), limit(100))
    const unsubs = [
      onSnapshot(q1, snap => setNotifs(prev => {
        const list = snap.docs.map(d => ({ id: d.id, ...d.data() }))
        const merged = new Map(prev.map(n => [n.id, n]))
        list.forEach(n => merged.set(n.id, n))
        return Array.from(merged.values())
      })),
      onSnapshot(q2, snap => setNotifs(prev => {
        const list = snap.docs.map(d => ({ id: d.id, ...d.data() }))
        const merged = new Map(prev.map(n => [n.id, n]))
        list.forEach(n => merged.set(n.id, n))
        return Array.from(merged.values())
      }))
    ]
    return () => unsubs.forEach(u => u())
  }, [open, uid])

  const handleDeleteNotification = async (notificationId) => {
    if (!uid || !notificationId) return
    
    setDeleting(notificationId)
    try {
      await deleteDoc(doc(db, "notifications", notificationId))
      // The notification will be automatically removed from the list via the onSnapshot listener
    } catch (error) {
      console.error("Error deleting notification:", error)
      // You could add a toast notification here for error feedback
    } finally {
      setDeleting(null)
    }
  }

  if (!open) return null
  return (
    <div className="fixed inset-0 z-[1100] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-[1px]" onClick={onClose} />
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md mx-auto p-6 z-10">
        <button className="absolute right-2 top-2 p-2 rounded hover:bg-gray-100" aria-label="Close" onClick={onClose}>✕</button>
        <h3 className="font-semibold mb-4 text-lg">Notifications</h3>
        {notifs.length === 0 ? (
          <p className="text-sm text-ink/60">No notifications yet.</p>
        ) : (
          <ul className="space-y-3 max-h-80 overflow-y-auto pr-2">
            {notifs.sort((a, b) => {
              const ta = a?.createdAt?.toMillis?.() ? a.createdAt.toMillis() : 0
              const tb = b?.createdAt?.toMillis?.() ? b.createdAt.toMillis() : 0
              return tb - ta
            }).map(n => (
              <li key={n.id} className="relative group">
                <NotificationCard type="info" className="mb-0">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="font-medium text-sm">{n.message || n.type}</div>
                      <div className="text-xs text-ink/50 mt-1">
                        {n.statusTo ? `Status: ${n.statusTo}` : null}
                        {n.createdAt?.toDate ? ` • ${n.createdAt.toDate().toLocaleString()}` : null}
                      </div>
                    </div>
                    <button
                      onClick={() => handleDeleteNotification(n.id)}
                      disabled={deleting === n.id}
                      className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 p-1 text-ink/40 hover:text-red-500 hover:bg-red-50 rounded"
                      title="Delete notification"
                      aria-label="Delete notification"
                    >
                      {deleting === n.id ? (
                        <div className="w-4 h-4 border-2 border-red-500 border-t-transparent rounded-full animate-spin"></div>
                      ) : (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      )}
                    </button>
                  </div>
                </NotificationCard>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}
